/**
 * schoolingMotes — many small shapes moving as a loose FLOCK (boids-lite:
 * cohesion/alignment/separation). The "living swarm" primitive — distinct from
 * `critters` (independent traversers) and `fireflies` (drifting motes). Adds
 * life to any biome; the small-scale companion to `leviathans`.
 *
 * Boids: each mote steers toward the flock center (cohesion), aligns with the
 * average heading (alignment), and avoids neighbors (separation). Bounded to
 * the vertical band; wraps at horizontal edges. Cheap: O(n) with sampled
 * neighbors (not O(n²)); no per-frame allocations.
 *
 * Degradation (BUILD_STANDARDS §3): createGlowSprite returns null if textures
 * not ready → break. Budget exhausted → break. Reduce-motion → slow to a
 * near-stop hover (no darting). Optional `startle`: on COMBAT_MOOD spike,
 * briefly raise separation/speed (the school scatters) → reactive life.
 *
 * VQA (BUILD_STANDARDS §2): spriteCount() returns the mote count. REGION
 * crop = the school's band. Contact strip for the flock motion.
 *
 * @see docs/design/systems/schooling-motes-kind.md — blueprint.
 */
import { registerEffect, makeContentHandle, type EffectBuilder } from '../effectBuilders';
import { seededRand } from '../atmospherePlacement';
import { createGlowSprite } from '../../vfx/glowSpriteFactory';
import { vfxBudgetManager } from '../../VFXBudgetManager';
import { getMotionPrefs } from '../../../systems/accessibility/motionPrefs';
import { Z_LAYERS } from '../../../rendering/Z_LAYERS';
import { lerp } from '../../../../shared/utils/MathUtils';
import type { ISprite } from '../../../rendering/IRenderer';
import type { SchoolingMotesSpec } from '../../../../config/atmosphere/effectLayer.types';

/** Boids defaults (BUILD_STANDARDS §1 — named constants, not inline). */
const DEFAULT_COHESION = 0.3;
const DEFAULT_ALIGNMENT = 0.4;
const DEFAULT_SEPARATION = 0.5;
const NEIGHBOR_SAMPLE_SIZE = 6;  // sample N neighbors (not full O(n²))
const SEPARATION_RADIUS = 30;     // px — avoid crowding within this distance
const BAND_Y_DEFAULT_MIN = 0.3;
const BAND_Y_DEFAULT_MAX = 0.7;
const REDUCE_MOTION_SPEED_MULT = 0.05;  // near-stop hover
const STARTLE_DURATION_MS = 1500;
const STARTLE_SEPARATION_MULT = 3;
const STARTLE_SPEED_MULT = 2;

/** One boid mote. */
interface Boid {
  sprite: ISprite;
  x: number;
  y: number;
  vx: number;
  vy: number;
}

const buildSchoolingMotes: EffectBuilder = (layer, ctx) => {
  if (layer.kind !== 'schoolingMotes') return null;
  const spec = layer as SchoolingMotesSpec;
  const { region } = ctx;
  const reduceMotion = getMotionPrefs().reduceMotion;
  const bandYMin = spec.bandYMin ?? BAND_Y_DEFAULT_MIN;
  const bandYMax = spec.bandYMax ?? BAND_Y_DEFAULT_MAX;
  const bandY0 = region.y0 + bandYMin * (region.y1 - region.y0);
  const bandY1 = region.y0 + bandYMax * (region.y1 - region.y0);
  const cohesion = spec.cohesion ?? DEFAULT_COHESION;
  const alignment = spec.alignment ?? DEFAULT_ALIGNMENT;
  const separation = spec.separation ?? DEFAULT_SEPARATION;

  const boids: Boid[] = [];
  const schoolCount = spec.schools ?? 1;
  const perSchool = Math.ceil(spec.count / schoolCount);

  for (let s = 0; s < schoolCount; s += 1) {
    // Each school starts in a cluster.
    const clusterX = lerp(region.x0, region.x1, seededRand(ctx.seed + s * 53.7));
    const clusterY = lerp(bandY0, bandY1, seededRand(ctx.seed + s * 53.7 + 1));
    for (let i = 0; i < perSchool; i += 1) {
      if (!vfxBudgetManager.canSpawnShape()) break;
      const seed = ctx.seed + s * 1000 + i * 11.3;
      const size = lerp(spec.sizeMin, spec.sizeMax, seededRand(seed));
      const color = spec.palette[Math.floor(seededRand(seed + 1) * spec.palette.length) % spec.palette.length];
      // Start near the cluster center with a small random offset.
      const x = clusterX + (seededRand(seed + 2) - 0.5) * 60;
      const y = clusterY + (seededRand(seed + 3) - 0.5) * 40;
      const angle = seededRand(seed + 4) * Math.PI * 2;
      const speed = spec.speed * (reduceMotion ? REDUCE_MOTION_SPEED_MULT : 1);
      const sprite = createGlowSprite({
        x, y, size, color, alpha: 0.8,
        textureKey: spec.textureKey, parent: ctx.layerContainer,
      });
      if (!sprite) break;
      boids.push({
        sprite,
        x, y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
      });
    }
  }

  if (!boids.length) return null;

  // Reduced motion: place statically, no tick (near-stop hover).
  if (reduceMotion) {
    return makeContentHandle({
      sprites: boids.map((b) => b.sprite),
      spriteCount: () => boids.length,
      onDestroy: () => {
        for (const b of boids) { try { b.sprite.destroy(); } catch { /* gone */ } }
      },
    });
  }

  // Boids tick: cohesion + alignment + separation, bounded to the band.
  let startleMsLeft = 0;
  const tick = (dtMs: number) => {
    if (startleMsLeft > 0) startleMsLeft -= dtMs;
    const dtSec = Math.min(dtMs / 1000, 0.05); // clamp dt for stability
    const sepMult = startleMsLeft > 0 ? STARTLE_SEPARATION_MULT : 1;
    const spdMult = startleMsLeft > 0 ? STARTLE_SPEED_MULT : 1;
    const spanX0 = region.x0 - 50;
    const spanX1 = region.x1 + 50;

    for (let i = 0; i < boids.length; i += 1) {
      const b = boids[i];
      let cx = 0, cy = 0, ax = 0, ay = 0, sx = 0, sy = 0, neighbors = 0, closeNeighbors = 0;

      // Sample N neighbors (not full O(n²)) — pick stride-cycled indices.
      for (let j = 0; j < NEIGHBOR_SAMPLE_SIZE; j += 1) {
        const ni = (i + 1 + j * 3) % boids.length;
        if (ni === i) continue;
        const n = boids[ni];
        const dx = n.x - b.x;
        const dy = n.y - b.y;
        cx += n.x; cy += n.y;
        ax += n.vx; ay += n.vy;
        neighbors += 1;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < SEPARATION_RADIUS && dist > 0) {
          // Steer away from close neighbors.
          sx -= dx / dist;
          sy -= dy / dist;
          closeNeighbors += 1;
        }
      }

      if (neighbors > 0) {
        cx /= neighbors; cy /= neighbors;
        ax /= neighbors; ay /= neighbors;
        // Cohesion: steer toward center.
        const cohFx = (cx - b.x) * cohesion * 0.01;
        const cohFy = (cy - b.y) * cohesion * 0.01;
        // Alignment: steer toward average heading.
        const alignFx = (ax - b.vx) * alignment * 0.05;
        const alignFy = (ay - b.vy) * alignment * 0.05;
        // Separation: steer away from close neighbors.
        const sepFx = sx * separation * sepMult * 0.5;
        const sepFy = sy * separation * sepMult * 0.5;
        b.vx += (cohFx + alignFx + sepFx) * dtSec * 60;
        b.vy += (cohFy + alignFy + sepFy) * dtSec * 60;
      }

      // Clamp speed.
      const maxSpeed = spec.speed * spdMult;
      const spd = Math.sqrt(b.vx * b.vx + b.vy * b.vy);
      if (spd > maxSpeed) {
        b.vx = (b.vx / spd) * maxSpeed;
        b.vy = (b.vy / spd) * maxSpeed;
      }

      // Integrate.
      b.x += b.vx * dtSec;
      b.y += b.vy * dtSec;

      // Wrap horizontally, bounce vertically within the band.
      if (b.x < spanX0) b.x = spanX1;
      else if (b.x > spanX1) b.x = spanX0;
      if (b.y < bandY0) { b.y = bandY0; b.vy = Math.abs(b.vy); }
      else if (b.y > bandY1) { b.y = bandY1; b.vy = -Math.abs(b.vy); }

      b.sprite.setPosition(b.x, b.y);
    }
  };
  const unsubTick = ctx.addTick(tick);

  // Optional startle: listen for COMBAT_MOOD to scatter the school.
  let unsubCombat: (() => void) | null = null;
  if (spec.startle) {
    try {
      const { EventBus, EVENTS } = require('../../../../shared/EventBus');
      unsubCombat = EventBus.getInstance().on(EVENTS.COMBAT_MOOD, () => {
        startleMsLeft = STARTLE_DURATION_MS;
      });
    } catch { /* EventBus not available in test env */ }
  }

  return makeContentHandle({
    sprites: boids.map((b) => b.sprite),
    spriteCount: () => boids.length,
    onDestroy: () => {
      try { unsubTick(); } catch { /* gone */ }
      try { unsubCombat?.(); } catch { /* gone */ }
      for (const b of boids) { try { b.sprite.destroy(); } catch { /* gone */ } }
    },
  });
};

registerEffect({
  kind: 'schoolingMotes',
  build: buildSchoolingMotes,
  depth: Z_LAYERS.ATMOSPHERE,
  mooded: true,
});
